import { AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';
import type { SetupStepStatus } from './setup-step-meta';

const STATUS_COPY: Record<SetupStepStatus, { label: string; tone: string }> = {
  ok: {
    label: 'Ready',
    tone: 'text-status-success',
  },
  error: {
    label: 'Error',
    tone: 'text-status-error',
  },
  pending: {
    label: 'Pending',
    tone: 'text-status-accent-orange',
  },
};

interface SetupStatusBadgeProps {
  status: SetupStepStatus;
  className?: string;
}

/**
 * Icon-driven status badge used by both the first-run wizard and the admin
 * plugin-detail page so the two surfaces stay in lock-step on shape, label,
 * and color when a plugin's setup state changes.
 */
export function SetupStatusBadge({ status, className }: SetupStatusBadgeProps) {
  const { label, tone } = STATUS_COPY[status];
  const Icon = status === 'ok' ? CheckCircle2 : status === 'error' ? AlertTriangle : Circle;
  return (
    <Badge
      variant="secondary"
      data-status={status}
      data-testid="setup-status-badge"
      className={cn('gap-1 text-xs font-normal', tone, className)}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}
